import type {
  AcquireBookPictures,
  BookTypeEng,
  PictureRequest,
} from "@/types/acquirebook";
import { generateUniqueId } from "@/utils/generateUniqueId";
import { getPrefectureIdByValue } from "@/utils/utilsAcquireBook";
import {
  normalizeRealEstateType,
  stripPrefecturePrefix,
} from "@/features/inheritanceReuse/utils/buildAcquireUrl";
import type { RegistryChangeAlertEntity } from "@/features/registryChangeAlert/types";

/** 登記取得依頼（/picture-request/）の送信内容。 */
export interface ReuseAcquireRequestPayload {
  withOwnerAnalyze: boolean;
  withAllMattersAnalyze: boolean;
  identificationName: string;
  items: PictureRequest[];
}

/** 種類ごとの件数（確認モーダルの表示用）。 */
export interface ReuseAcquireCounts {
  shoyushajiko: number;
  zenbujiko: number;
}

/**
 * 取得依頼に必要な都道府県と不動産種別を特定できるか。
 * 特定できない行を既定値で依頼に含めると、1 件のために依頼全体が入力エラーになるため、依頼の対象から外す。
 */
export const canBuildReuseAcquireItem = (
  alert: RegistryChangeAlertEntity
): boolean =>
  getPrefectureIdByValue(alert.prefectureName) !== undefined &&
  normalizeRealEstateType(alert.realEstateType) !== undefined;

/**
 * アラート 1 件を、新しい登記をリユースするだけの取得依頼 1 件へ変換する。
 * reuse_pictures で新しい登記の明細 ID を直接指定し、新規取得（get_new_picture）はしない。
 */
export const toReuseAcquireItem = (
  alert: RegistryChangeAlertEntity
): PictureRequest => {
  const bookType: BookTypeEng =
    (normalizeRealEstateType(alert.realEstateType) as
      | BookTypeEng
      | undefined) ?? "LAND";
  const prefId = getPrefectureIdByValue(alert.prefectureName);
  const picture = alert.pictureType as AcquireBookPictures;
  return {
    real_estate_type: bookType,
    pref_code: prefId !== undefined ? String(prefId).padStart(2, "0") : "",
    location_full: stripPrefecturePrefix(alert.location, alert.prefectureName),
    chiban_kaoku_number: alert.chibanKaokuNumber,
    past_days: 0,
    pictures: [picture],
    zenbujiko_options: [],
    reuse_pictures: [
      {
        picture,
        reuseDetailId: alert.newerDetailId,
        getNewPicture: false,
      },
    ],
    unique_id: generateUniqueId(),
  };
};

/**
 * 選んだアラートのリユース取得依頼を組み立てる。
 * @param alerts 選んだアラート
 * @param identificationName 識別名
 */
export const buildReuseAcquireRequest = (
  alerts: RegistryChangeAlertEntity[],
  identificationName: string
): ReuseAcquireRequestPayload => ({
  withOwnerAnalyze: false,
  withAllMattersAnalyze: false,
  identificationName,
  items: alerts.map(toReuseAcquireItem),
});

/**
 * 取得依頼 API の上限に収まるよう、アラートを順に分割する。
 * @param alerts 選んだアラート
 * @param size 1 回の依頼に載せる件数
 */
export const chunkAlerts = (
  alerts: RegistryChangeAlertEntity[],
  size: number
): RegistryChangeAlertEntity[][] => {
  const chunks: RegistryChangeAlertEntity[][] = [];
  for (let start = 0; start < alerts.length; start += size) {
    chunks.push(alerts.slice(start, start + size));
  }
  return chunks;
};

/** 選んだアラートの種類ごとの件数を数える。 */
export const countByPictureType = (
  alerts: RegistryChangeAlertEntity[]
): ReuseAcquireCounts => ({
  shoyushajiko: alerts.filter((alert) => alert.pictureType === "SHOYUSHAJIKO")
    .length,
  zenbujiko: alerts.filter((alert) => alert.pictureType === "ZENBUJIKO").length,
});
